// Pure context definition + hook for the pending generations feature.
// Kept in a non-`.tsx` file so that the matching Provider component
// stays component-only and React Fast Refresh does not invalidate it.
import { createContext, useContext } from 'react';
import type { TestConfig as TestConfigType } from '@/types/oposiciones';
import type {
  Cuerpo,
  GenType,
  PendingGen,
} from './pendingGenerations.constants';

// Type re-exports so consumers can import everything from this single module.
export type { Cuerpo, GenType, GenStatus, PendingGen } from './pendingGenerations.constants';

export interface RunTestArgs {
  cuerpo: Cuerpo;
  userId: string;
  testConfig: TestConfigType;
  topicName?: string;
}

export interface RegisterReadyArgs {
  type: GenType;
  cuerpo: Cuerpo;
  topic: string;
  resultId?: string;
}

export interface RunCaseArgs {
  cuerpo: Cuerpo;
  userId: string;
  topicId: string;
  topicLabel?: string;
  difficulty?: string;
  caseMode?: 'dual' | 'juridico' | 'economico';
}

export interface RunFlashcardsArgs {
  cuerpo: Cuerpo;
  userId: string;
  topic: string;
  topicLabel?: string;
  count: number;
}

export interface PendingGenContextValue {
  generations: PendingGen[];
  start: (args: { type: GenType; cuerpo: Cuerpo; topic: string; id?: string }) => string;
  markReady: (id: string, resultId: string, fullRoute?: string) => void;
  markError: (id: string, message?: string) => void;
  markSeen: (id: string) => void;
  clear: (id: string) => void;
  runBackgroundTest: (args: RunTestArgs) => string;
  runBackgroundCase: (args: RunCaseArgs) => string;
  runBackgroundFlashcards: (args: RunFlashcardsArgs) => string;
  registerReady: (args: RegisterReadyArgs) => string;
  runBackgroundGamma: (args: { topic: string; topicId?: string; format: 'presentation' | 'infografia'; cuerpo: Cuerpo; userId?: string; mode?: string; content?: string }) => string;
  runBackgroundResumen: (args: { topic: string; topicId?: string; cuerpo: Cuerpo; userId?: string; mode?: string; exportType?: 'word' | 'pdf'; content?: string }) => string;
}

export const PendingGenContext = createContext<PendingGenContextValue | null>(null);

const FALLBACK: PendingGenContextValue = {
  generations: [],
  start: () => '',
  markReady: () => {},
  markError: () => {},
  markSeen: () => {},
  clear: () => {},
  runBackgroundTest: () => '',
  runBackgroundCase: () => '',
  runBackgroundFlashcards: () => '',
  registerReady: () => '',
  runBackgroundGamma: () => '',
  runBackgroundResumen: () => '',
};

export function usePendingGenerations(): PendingGenContextValue {
  const ctx = useContext(PendingGenContext);
  return ctx ?? FALLBACK;
}
